import React, {FC, useCallback, useState} from 'react';

import {
  useDebounce,
  useLazyUnmountCancelToken,
} from '../../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {useInitialEffect} from '../../../../libs/shared/core/src/helpers/react/hooks/useEffectHelpers';
import {AsyncStatusesEnum} from '../../../../libs/shared/core/src/helpers/types/asyncStatuses';
import {
  AsyncData,
  asyncDataFailed,
  asyncDataLoaded,
  asyncDataLoading,
} from '../../../../libs/shared/ui/src/components/loaders/asyncDataHelpers';
import {useAppDispatch} from '../../../helpers/appReduxHelpers';
import {ConversationModel} from '../../../models/api/conversationModel';
import {fetchConversationAsync} from '../../../store/data/conversations/conversationsActions';
import {
  ConversationPreviewContainer,
  ConversationPreviewContainerProps,
} from './conversationPreviewContainer';

interface AsyncConversationPreviewerProps extends Omit<ConversationPreviewContainerProps, 'conversation'> {
  conversationId: number;
}

export const AsyncConversationPreviewer: FC<AsyncConversationPreviewerProps> = (props) => {
  const {conversationId} = props;
  const [conversationAsyncData, setConversationAsyncData] =
    useState<AsyncData<ConversationModel>>(asyncDataLoading());
  const dispatch = useAppDispatch();
  const buildUnmountCancelToken = useLazyUnmountCancelToken();

  const fetchConversation = useCallback(async () => {
    try {
      const resultConversation = await dispatch(
        fetchConversationAsync(conversationId, buildUnmountCancelToken()),
      );
      setConversationAsyncData(asyncDataLoaded(resultConversation));
    } catch (e) {
      setConversationAsyncData(asyncDataFailed(e));
    }
  }, [buildUnmountCancelToken, conversationId, dispatch]);

  const fetchConversationDebounced = useDebounce(fetchConversation, 200);

  useInitialEffect(() => {
    fetchConversationDebounced();
  });

  if (conversationAsyncData.status !== AsyncStatusesEnum.LOADED) {
    return null;
  }

  return (
    <ConversationPreviewContainer
      conversation={conversationAsyncData.data}
      panelId={props.panelId}
      anchor={props.anchor}
      viewerConversationId={props.viewerConversationId}
    />
  );
};
